/**
 * Incidents API — Common Types
 *
 * Shared shapes used across incident endpoints. Endpoint-specific
 * request/response schemas land in U3.
 */

import { z } from 'zod'

import {
  IncidentSchema,
  IncidentSnapshotSchema,
  IncidentClaimSchema,
  ClaimTypeSchema
} from '../../schemas/entities/incident'

/**
 * Request body shared by claim-create variants.
 *
 * Bulk path:    { participantIds: [...] }
 * Single path:  { participantId: '...' }
 *
 * `claimType` defaults to STAFF on the server when the caller is a
 * faculty/staff/admin and is omitted by the client.
 */
export const CreateClaimBodySchema = z
  .object({
    participantId: z.string().optional(),
    participantIds: z.array(z.string()).min(1).optional(),
    claimType: ClaimTypeSchema.optional(),
    note: z.string().max(500).optional()
  })
  .refine(
    v => Boolean(v.participantId) !== Boolean(v.participantIds),
    'Provide exactly one of participantId or participantIds'
  )
export type CreateClaimBody = z.infer<typeof CreateClaimBodySchema>

/**
 * Response envelope shared by single-incident reads.
 */
export const IncidentResponseSchema = z.object({
  data: IncidentSchema
})
export type IncidentResponse = z.infer<typeof IncidentResponseSchema>

export const IncidentSnapshotResponseSchema = z.object({
  data: IncidentSnapshotSchema
})
export type IncidentSnapshotResponse = z.infer<typeof IncidentSnapshotResponseSchema>

export const IncidentClaimResponseSchema = z.object({
  data: IncidentClaimSchema
})
export type IncidentClaimResponse = z.infer<typeof IncidentClaimResponseSchema>
